import React from 'react';
import { Link } from 'react-router-dom';
import { PublicNavbar } from '@/components/Layout/PublicNavbar';

/**
 * Aviso de Privacidad Integral (LFPDPPP, Art. 16 y concordantes).
 *
 * BORRADOR LEGAL — pendiente de revisión por abogado mexicano antes de su
 * publicación oficial. Los placeholders ("Coplaner Inc", domicilio, correos
 * de privacidad y oficial de privacidad) deben ser sustituidos por la
 * información definitiva de la sociedad mercantil antes del lanzamiento.
 */
const PrivacyNotice: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      <main className="pt-24 pb-16">
        <article className="max-w-3xl mx-auto px-6 py-12 text-base leading-7 text-neutral-800">
          <header className="mb-10">
            <h1 className="text-h1 font-bold text-brand-primary mb-3">
              Aviso de Privacidad
            </h1>
            <p className="text-body-sm text-neutral-500">
              Última actualización: 13 de mayo de 2026
            </p>
            <p className="mt-4">
              El presente Aviso de Privacidad se emite en cumplimiento de la
              Ley Federal de Protección de Datos Personales en Posesión de los
              Particulares (LFPDPPP), su Reglamento y los Lineamientos del
              Aviso de Privacidad emitidos por el INAI.
            </p>
          </header>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              1. Identidad y domicilio del responsable
            </h2>
            <p>
              <strong>Coplaner Inc</strong> (en adelante, &laquo;CoPlanner&raquo;)
              es el responsable del tratamiento de sus datos personales, con
              domicilio en{' '}
              <strong>
                Av. Reforma 123, Col. Juárez, Alcaldía Cuauhtémoc, C.P. 06600,
                Ciudad de México, México
              </strong>{' '}
              (placeholder, pendiente de confirmación).
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              2. Datos personales que se recaban
            </h2>
            <p>
              <strong>De los Planners</strong> (clientes que contratan el
              servicio): nombre completo, correo electrónico, contraseña
              (almacenada en forma cifrada mediante hash), datos de pago
              (procesados directamente por Stripe; CoPlanner no almacena el
              número completo de la tarjeta), RFC y razón social (opcionales,
              para emisión de CFDI), y datos de uso del servicio (registros de
              acceso, dirección IP, navegador, acciones realizadas dentro de la
              plataforma).
            </p>
            <p>
              <strong>De los Invitados</strong> (titulares cuyos datos son
              cargados por los Planners para la gestión de eventos): nombre,
              número de teléfono con WhatsApp, estatus de confirmación (RSVP) y
              los mensajes intercambiados a través de la plataforma con el
              Planner.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              3. Datos personales sensibles
            </h2>
            <p>
              CoPlanner manifiesta de manera expresa que <strong>no recaba
              datos personales sensibles</strong> conforme a la definición del
              artículo 3, fracción VI de la LFPDPPP (origen racial o étnico,
              estado de salud, información genética, creencias religiosas,
              filosóficas o morales, afiliación sindical, opiniones políticas,
              preferencia sexual, etc.). En caso de que en el futuro fuera
              necesario tratar datos sensibles, se solicitará previamente el
              consentimiento expreso por escrito del titular.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              4. Finalidades del tratamiento
            </h2>
            <p>
              <strong>Finalidades primarias</strong> (necesarias para la
              prestación del servicio y origen de la relación jurídica):
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Crear y administrar la cuenta del Planner.</li>
              <li>
                Autenticar al Planner cada vez que accede a la plataforma.
              </li>
              <li>
                Procesar pagos, generar facturas y emitir comprobantes fiscales
                digitales (CFDI).
              </li>
              <li>
                Enviar mensajes por WhatsApp a los Invitados en nombre del
                Planner, conforme a las instrucciones del propio Planner.
              </li>
              <li>
                Brindar soporte técnico y atención a usuarios.
              </li>
              <li>
                Dar cumplimiento a obligaciones legales, fiscales y a
                requerimientos de autoridad competente.
              </li>
            </ul>
            <p>
              <strong>Finalidades secundarias</strong> (no necesarias para el
              servicio):
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Generar métricas agregadas y anonimizadas de uso de la
                plataforma.
              </li>
              <li>
                Mejorar las funcionalidades y la experiencia de usuario del
                producto.
              </li>
            </ul>
            <p>
              <strong>
                El titular puede oponerse a las finalidades secundarias
              </strong>{' '}
              enviando un correo a la dirección señalada en la sección 8 del
              presente Aviso, sin que ello afecte la prestación del servicio
              que ha contratado.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              5. Transferencias de datos
            </h2>
            <p>
              CoPlanner transfiere datos personales a los siguientes terceros,
              que actúan como encargados o como responsables conjuntos, con la
              finalidad estricta de prestar el servicio:
            </p>
            <ul className="list-disc pl-6 space-y-3">
              <li>
                <strong>Supabase Inc.</strong> (Estados Unidos de América) —
                proveedor de hospedaje de la base de datos y servicios de
                autenticación. Base de legitimidad: necesario para la
                prestación del servicio (Art. 37, fracción V de la LFPDPPP).
              </li>
              <li>
                <strong>Twilio Inc.</strong> (Estados Unidos de América) —
                enrutamiento de mensajes a través de la API de WhatsApp
                Business. Base de legitimidad: necesario para la prestación del
                servicio (Art. 37, fracción V).
              </li>
              <li>
                <strong>Stripe, Inc. / Stripe Payments Europe, Ltd.</strong>{' '}
                (Estados Unidos de América / Irlanda) — procesamiento de pagos
                con tarjeta. Base de legitimidad: necesario para el
                cumplimiento de la relación contractual (Art. 37, fracción V).
              </li>
              <li>
                <strong>Functional Software, Inc. (Sentry)</strong> (Estados
                Unidos de América) — monitoreo de errores técnicos del
                software. Base de legitimidad: necesario para la prestación
                del servicio y la seguridad de la información (Art. 37,
                fracción V).
              </li>
              <li>
                <strong>Intuition Machines, Inc. (hCaptcha)</strong> (Estados
                Unidos de América) — verificación anti-bot durante el registro
                de Planners. Base de legitimidad: necesario para la seguridad
                de la plataforma (Art. 37, fracción V); el titular consiente
                este tratamiento al completar el registro (Art. 36).
              </li>
            </ul>
            <p>
              Estas transferencias no requieren del consentimiento expreso del
              titular conforme a la LFPDPPP, en virtud de las hipótesis
              señaladas. CoPlanner se obliga a celebrar con sus encargados los
              instrumentos jurídicos necesarios para garantizar un nivel
              equivalente de protección de los datos.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              6. Derechos ARCO
            </h2>
            <p>
              Usted, o su representante legal debidamente acreditado, podrá
              ejercer en todo momento sus derechos de <strong>Acceso,
              Rectificación, Cancelación u Oposición</strong> (Derechos ARCO),
              así como revocar el consentimiento que haya otorgado para el
              tratamiento de sus datos personales.
            </p>
            <p>
              Para ejercer dichos derechos, envíe una solicitud al correo
              electrónico{' '}
              <a
                href="mailto:privacidad@coplaner.mx"
                className="text-brand-primary hover:text-brand-primary-hover underline"
              >
                privacidad@coplaner.mx
              </a>{' '}
              que incluya, como mínimo:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Nombre completo del titular y un medio para comunicarle la
                respuesta.
              </li>
              <li>
                Documentos que acrediten su identidad (identificación oficial)
                o, en su caso, la representación legal del titular.
              </li>
              <li>
                Descripción clara y precisa de los datos personales sobre los
                que se busca ejercer el derecho.
              </li>
              <li>
                Motivo de la solicitud y, en su caso, cualquier otro elemento
                que facilite la localización de los datos.
              </li>
            </ul>
            <p>
              CoPlanner dará respuesta a su solicitud en un plazo máximo de
              <strong> 20 (veinte) días hábiles</strong> a partir de su
              recepción, conforme al artículo 32 de la LFPDPPP. Si la respuesta
              es procedente, se hará efectiva dentro de los 15 (quince) días
              hábiles siguientes.
            </p>
            <p>
              Tratándose de Invitados cuyos datos fueron cargados por un
              Planner, CoPlanner canalizará la solicitud al Planner
              correspondiente, en su calidad de responsable directo de dichos
              datos, sin perjuicio de las medidas que CoPlanner pueda adoptar
              como encargado o responsable conjunto.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              7. Opciones para limitar el uso o divulgación de los datos
            </h2>
            <p>
              Puede revocar el consentimiento que nos haya otorgado, así como
              dar de baja su cuenta, mediante alguno de los siguientes medios:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Desde la sección de cuenta de la aplicación (
                <code className="px-1 py-0.5 bg-neutral-100 rounded text-body-sm">
                  /app/settings/account
                </code>
                ), una vez disponible la opción de autoservicio.
              </li>
              <li>
                Mientras dicha opción esté en desarrollo, escribiendo a{' '}
                <a
                  href="mailto:privacidad@coplaner.mx"
                  className="text-brand-primary hover:text-brand-primary-hover underline"
                >
                  privacidad@coplaner.mx
                </a>{' '}
                con su solicitud.
              </li>
            </ul>
            <p>
              La revocación del consentimiento no tendrá efectos retroactivos y
              no afectará operaciones de tratamiento que hubieran sido
              necesarias para el cumplimiento de obligaciones legales o
              contractuales preexistentes.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              8. Cambios al Aviso de Privacidad
            </h2>
            <p>
              CoPlanner se reserva el derecho de actualizar este Aviso para
              reflejar cambios en su operación, en la legislación aplicable o
              en sus prácticas de privacidad. Los cambios se publicarán en esta
              misma página, con su fecha de &laquo;Última actualización&raquo;.
              Cuando los cambios sean materiales, CoPlanner los notificará por
              correo electrónico a los Planners con al menos 30 (treinta) días
              naturales de antelación a su entrada en vigor.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              9. Cookies y tecnologías similares
            </h2>
            <p>
              La plataforma utiliza las siguientes tecnologías de almacenamiento
              en el navegador del usuario:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Cookies de sesión</strong> y tokens equivalentes,
                estrictamente necesarios para mantener al Planner autenticado
                durante el uso de la aplicación.
              </li>
              <li>
                <strong>localStorage</strong>, utilizado para almacenar
                parámetros de atribución de campañas de marketing (clave{' '}
                <code className="px-1 py-0.5 bg-neutral-100 rounded text-body-sm">
                  signup_attribution
                </code>
                ) con el fin de medir la efectividad de nuestros canales de
                adquisición.
              </li>
            </ul>
            <p>
              CoPlanner <strong>no utiliza cookies de seguimiento publicitario
              de terceros</strong> (como pixeles de Meta o Google Ads) en la
              versión actual del producto. Si en el futuro se incorporaran,
              este Aviso será actualizado con la información correspondiente.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              10. Oficial de privacidad
            </h2>
            <p>
              CoPlanner ha designado a un oficial de privacidad encargado del
              cumplimiento de la LFPDPPP y de atender las solicitudes de los
              titulares:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Nombre</strong>: Por designar (placeholder, pendiente
                de confirmación).
              </li>
              <li>
                <strong>Correo electrónico</strong>:{' '}
                <a
                  href="mailto:privacidad@coplaner.mx"
                  className="text-brand-primary hover:text-brand-primary-hover underline"
                >
                  privacidad@coplaner.mx
                </a>
                .
              </li>
            </ul>
            <p>
              Si considera que su derecho a la protección de datos personales
              ha sido vulnerado y no obtiene respuesta satisfactoria por parte
              de CoPlanner, puede acudir al Instituto Nacional de Transparencia,
              Acceso a la Información y Protección de Datos Personales (INAI)
              en{' '}
              <a
                href="https://home.inai.org.mx/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary hover:text-brand-primary-hover underline"
              >
                home.inai.org.mx
              </a>
              .
            </p>
          </section>

          <div className="border-t border-neutral-200 pt-6">
            <Link
              to="/"
              className="text-brand-primary hover:text-brand-primary-hover underline focus-visible-ring"
            >
              &larr; Volver al inicio
            </Link>
          </div>
        </article>
      </main>
    </div>
  );
};

export default PrivacyNotice;
